import {
  Box,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { IFileBase, IFileManager } from '@subflow-frontend/@types/file';
import FileThumbnail from '@subflow-frontend/components/file-thumbnail';
import Iconify from '@subflow-frontend/components/iconify';
import UploadBox from '@subflow-frontend/components/upload/UploadBox';
import { useUploadThumbnail } from '@subflow/data-access';
import React, { useCallback, useEffect, useState } from 'react';
import { DropzoneOptions } from 'react-dropzone';

interface UploadThumbnailProps<ID extends string | number = number> {
  file: IFileBase<ID>;
}

type TUploadState<ID extends string | number = number> =
  | { type: 'init' }
  | { type: 'preview'; file: IFileBase<ID> }
  | { type: 'upload'; isUploading: boolean; force?: boolean };
export const UploadThumbnail = <ID extends string | number = number>(
  props: UploadThumbnailProps<ID>
) => {
  const { file } = props;
  const [upload, setUpload] = useState<TUploadState<ID>>({
    type: 'init',
  });
  const { mutateAsync: uploadThumbnail } = useUploadThumbnail();
  const handleDrop = useCallback<DropzoneOptions['onDrop']>(
    async (acceptedFiles, _, event) => {
      try {
        event.stopPropagation();
        setUpload({
          type: 'upload',
          isUploading: true,
        });
        const uploadFile = acceptedFiles[0];
        await uploadThumbnail({
          thumbnail: uploadFile,
          fileName: uploadFile.name,
          contentType: uploadFile.type,
          contentId: Number(file.id),
        });
      } catch (e) {
        setUpload({
          type: 'upload',
          isUploading: false,
        });
      }
    },
    [file.id, uploadThumbnail]
  );

  const determineUploadState: (file: IFileBase<ID>) => TUploadState<ID> = (
    file
  ) => {
    if (file.thumbnailUrl) {
      return {
        type: 'preview',
        file,
      };
    } else {
      return {
        type: 'upload',
        isUploading: false,
      };
    }
  };

  useEffect(() => {
    setUpload(determineUploadState(file));
  }, [file]);

  const handleMouseEnter = () => {
    if (upload.type === 'upload' && (upload.force || upload.isUploading))
      return;

    setUpload({
      type: 'upload',
      isUploading: false,
    });
  };

  const handleMouseLeave = () => {
    if (upload.type === 'upload' && (upload.force || upload.isUploading))
      return;

    setUpload(determineUploadState(file));
  };

  function handleEdit() {
    setUpload({
      type: 'upload',
      isUploading: false,
      force: true,
    });
  }

  return (
    <>
      <Stack>
        <Stack pt={1} pb={2}>
          <Divider sx={{ borderStyle: 'dashed' }} />
        </Stack>
        <Stack spacing={1}>
          <Stack flexDirection={'row'}>
            <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>
              Thumbnail
            </Typography>
            <IconButton
              sx={{ visibility: upload.type === 'preview' ? 'init' : 'hidden' }}
              size="small"
              onClick={handleEdit}
            >
              <Iconify icon={'material-symbols:edit'} />
            </IconButton>
          </Stack>
          <Stack>
            <Typography marginTop={0} variant="body2" sx={{ marginTop: 0 }}>
              Select or upload a picture.
            </Typography>
          </Stack>
          <Stack pt={1}>
            <Grid container spacing={0}>
              <Grid
                item
                xs={6}
                sx={{ position: 'relative' }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {upload.type === 'preview' && (
                  <Box sx={{ position: 'relative' }}>
                    <FileThumbnail
                      showPreview
                      file={upload.file}
                      sx={{ width: 128, height: 128, borderRadius: 1.5 }}
                      imgSx={{ borderRadius: 1 }}
                    />
                  </Box>
                )}
                {upload.type === 'upload' && (
                  <UploadBox
                    maxFiles={1}
                    onDrop={handleDrop}
                    placeholder={
                      <Stack
                        spacing={0.5}
                        alignItems="center"
                        sx={{ color: 'text.disabled' }}
                      >
                        <Iconify icon="eva:cloud-upload-fill" width={40} />
                        <Typography variant="body2" textAlign={'center'}>
                          {upload.isUploading
                            ? 'Uploading...'
                            : 'Upload Thumbnail'}
                        </Typography>
                      </Stack>
                    }
                    sx={{
                      width: '100%',
                      height: 128,
                      borderRadius: 1.5,
                      p: 0,
                      m: 0,
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};
